import { useEffect } from 'react'
import { NavLink as Link } from 'react-router'
import styled from '@emotion/styled'
import { useAppDispatch, useAppSelector } from 'src/redux/app/app.hooks'
import * as routes from 'src/constants/routes'
import RidersIcon from 'src/assets/images/nav/riders-icon.svg'
import HuntersIcon from 'src/assets/images/nav/hunters-icon.svg'
import VehiclesIcon from 'src/assets/images/nav/vehicles-icon.svg'
import CampaignsIcon from 'src/assets/images/nav/campaigns-icon.svg'
import ZonesIcon from 'src/assets/images/nav/zones-icon.svg'
import TuneIcon from 'src/assets/images/nav/tune-icon.svg'
import VmapIcon from 'src/assets/images/nav/vmap-icon.svg'
import ConnectivityIcon from 'src/assets/images/nav/ic_connectivity.svg'
import HomeIcon from 'src/assets/images/nav/home-icon.svg'
import BatteryIcon from 'src/assets/images/nav/battery.svg'
import PermissionsIcon from 'src/assets/images/nav/permissions-icon.svg'
import RideInspectorIcon from 'src/assets/images/nav/ic_search.svg'
import AccessControl from 'src/components/app/AccessControl'
import PartnerIcon from 'src/assets/images/nav/ic_api.svg'
import RepairIcon from 'src/assets/images/nav/ic_repairs.svg'
import CollapseIcon from 'src/assets/images/nav/IconCollapseMenu.svg'
import IconMarket from 'src/assets/images/city_regs.svg'
import {
    expandZone,
    setNavExpanded,
    expandRepairs,
    expandVehicles,
    expandBattery,
    selectIsZoneExpanded,
    selectIsNavOpen,
    selectIsRepairsExpanded,
    selectIsVehiclesExpanded,
    selectIsRidersExpanded,
    selectIsBatteryExpanded,
    expandRiders,
    selectIsMobileNavOpen,
    toggleMobileNav,
} from 'src/redux/nav'
import { ButtonText } from 'src/ui-kit/text/Text'
import * as colors from 'src/ui-kit/colors/colors'
import { permissions } from 'src/components/app/accessControl.permissions'
import { getPath } from 'src/components/app/utils/nav/navUtils'
import { SupportSection } from 'src/components/parts/navigation/SupportSection'
import { zIndex } from 'src/constants/zIndex'
import { AnimatedScooter } from 'src/components/parts/navigation/animatedScooter/AnimatedScooter'
import AnalyticsIcon from 'src/assets/images/nav/analytics-icon.svg'
import VoiAgentIcon from 'src/assets/images/nav/ic_voi.svg'
import { fontSecondary } from 'src/ui-kit/fonts/fontFamily'
import { ExpandableNavItem, Row } from 'src/components/parts/navigation/ExpandableNavItem'
import { HolidayAnimation } from 'src/components/holidayAnimation/HolidayAnimation'

const NAVBAR_EXPANDED_KEY = 'navbarExpanded'

interface Props {
    activeLink: string
    isMobile: boolean
}

export const MainNav = ({ activeLink, isMobile }: Props) => {
    const isZoneExpanded = useAppSelector(selectIsZoneExpanded)
    const isNavOpen = !!(useAppSelector(selectIsNavOpen) || isMobile)
    const isRepairsExpanded = useAppSelector(selectIsRepairsExpanded)
    const isRidersExpanded = useAppSelector(selectIsRidersExpanded)
    const isVehiclesExpanded = useAppSelector(selectIsVehiclesExpanded)
    const isBatteryExpanded = useAppSelector(selectIsBatteryExpanded)
    const isMobileNavOpen = useAppSelector(selectIsMobileNavOpen)

    const dispatch = useAppDispatch()

    useEffect(() => {
        const navVal = localStorage.getItem(NAVBAR_EXPANDED_KEY)
        if (navVal === null) {
            localStorage.setItem(NAVBAR_EXPANDED_KEY, 'true')
            dispatch(setNavExpanded(true))
        } else {
            dispatch(setNavExpanded(navVal === 'true'))
        }
    }, [dispatch])

    if (isMobile && !isMobileNavOpen) {
        return null
    }

    const expandNavbar = (value: boolean) => {
        localStorage.setItem(NAVBAR_EXPANDED_KEY, value.toString())
        dispatch(setNavExpanded(value))
    }

    const currentPath = getPath(activeLink)

    return (
        <NavigationWrapper $expanded={isNavOpen} $isMobileNav={isMobile}>
            {!isMobile && (
                <>
                    <HolidayAnimation />
                    <ExpandNavBtn $expanded={isNavOpen}>
                        <CollapseImg
                            data-testid='collapse-nav'
                            src={CollapseIcon}
                            onClick={() => expandNavbar(!isNavOpen)}
                        />
                    </ExpandNavBtn>
                </>
            )}
            <Content>
                <ButtonText $color={colors.coral} style={{ marginLeft: '20px', height: '20px' }}>
                    {isNavOpen && 'MENU'}
                </ButtonText>
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.OVERVIEW}
                    to={routes.OVERVIEW}
                    data-testid='overview-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={HomeIcon} alt='' />
                    {isNavOpen && 'HOME'}
                </NavItem>
                <AccessControl allow={permissions.hunter_editor}>
                    <NavItem
                        to={routes.HUNTERS}
                        data-testid='hunters-navigation-link'
                        $expanded={isNavOpen}
                        $active={currentPath === routes.HUNTERS || currentPath === routes.ORG}
                        onClick={() => dispatch(toggleMobileNav(false))}
                    >
                        <NavIcon $expanded={isNavOpen} src={HuntersIcon} alt='' />
                        {isNavOpen && 'HUNTERS'}
                    </NavItem>
                </AccessControl>
                <AccessControl allow={permissions.users_viewer}>
                    <ExpandableNavItem
                        to={routes.RIDERS}
                        $expanded={isNavOpen}
                        $active={currentPath === routes.RIDERS}
                        dataTestId='riders-navigation-link'
                        onClick={() => dispatch(toggleMobileNav(false))}
                        expandTestId='expanded-riders-navbar'
                        isExpanded={isRidersExpanded}
                        onExpandToggle={() => dispatch(expandRiders(!isRidersExpanded))}
                        showExpandButton={isNavOpen}
                    >
                        <Row style={{ justifyContent: !isNavOpen ? 'center' : undefined }}>
                            <NavIcon $expanded={isNavOpen} src={RidersIcon} alt='' />
                            {isNavOpen && 'RIDERS'}
                        </Row>
                    </ExpandableNavItem>
                    {isRidersExpanded && isNavOpen && (
                        <div>
                            <AccessControl allow={permissions.accident_editor}>
                                <NavItem
                                    $active={currentPath === routes.ACCIDENT_REPORTS}
                                    data-testid='accident-reports-navigation-link'
                                    to={routes.ACCIDENT_REPORTS}
                                    $small
                                    onClick={() => dispatch(toggleMobileNav(false))}
                                >
                                    - ACCIDENT REPORTS
                                </NavItem>
                            </AccessControl>
                        </div>
                    )}
                </AccessControl>
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.RIDE_INSPECTOR}
                    to={routes.RIDE_INSPECTOR}
                    data-testid='ride-inspector-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={RideInspectorIcon} alt='Ride Inspector' />
                    {isNavOpen && 'RIDE INSPECTOR'}
                </NavItem>
                <ExpandableNavItem
                    to={routes.VEHICLES}
                    $expanded={isNavOpen}
                    $active={currentPath === routes.VEHICLES}
                    dataTestId='vehicles-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                    expandTestId='expanded-scooters-navbar'
                    isExpanded={isVehiclesExpanded}
                    onExpandToggle={() => dispatch(expandVehicles(!isVehiclesExpanded))}
                    showExpandButton={isNavOpen}
                >
                    <Row style={{ justifyContent: !isNavOpen ? 'center' : undefined }}>
                        <NavIcon $expanded={isNavOpen} src={VehiclesIcon} alt='' />
                        {isNavOpen && 'VEHICLES'}
                    </Row>
                </ExpandableNavItem>
                {isVehiclesExpanded && isNavOpen && (
                    <>
                        <NavItem
                            $active={currentPath === routes.PARKING_PHOTO_LABELLING}
                            data-testid='parking-photo-labelling-navigation-link'
                            to={routes.PARKING_PHOTO_LABELLING}
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - PARKING PHOTO LABELLING
                        </NavItem>
                        <NavItem
                            $active={currentPath === routes.SAFETY_CONTROL_SYSTEM}
                            data-testid='safety-control-system-navigation-link'
                            to={routes.SAFETY_CONTROL_SYSTEM}
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - SAFETY CONTROL SYSTEM
                        </NavItem>
                    </>
                )}
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.VMAP}
                    to={routes.VMAP}
                    data-testid='vmap-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={VmapIcon} alt='' />
                    {isNavOpen && 'VEHICLE MAP'}
                </NavItem>
                <AccessControl allow={permissions.connectivity_viewer}>
                    <NavItem
                        $expanded={isNavOpen}
                        $active={currentPath === routes.FLEET_CONNECTIVITY}
                        to={routes.FLEET_CONNECTIVITY}
                        data-testid='fleet-connectivity-navigation-link'
                        onClick={() => dispatch(toggleMobileNav(false))}
                    >
                        <NavIcon $expanded={isNavOpen} src={ConnectivityIcon} alt='' />
                        {isNavOpen && 'FLEET CONNECTIVITY'}
                    </NavItem>
                </AccessControl>
                <ExpandableNavItem
                    to={routes.BATTERIES}
                    $expanded={isNavOpen}
                    $active={currentPath === routes.BATTERIES}
                    dataTestId='batteries-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                    expandTestId='expanded-battery-navbar'
                    isExpanded={isBatteryExpanded}
                    onExpandToggle={() => dispatch(expandBattery(!isBatteryExpanded))}
                    showExpandButton={isNavOpen}
                >
                    <Row style={{ justifyContent: !isNavOpen ? 'center' : undefined }}>
                        <NavIcon $expanded={isNavOpen} src={BatteryIcon} alt='' />
                        {isNavOpen && 'BATTERIES'}
                    </Row>
                </ExpandableNavItem>
                {isBatteryExpanded && isNavOpen && (
                    <>
                        <AccessControl allow={permissions.battery_editor}>
                            <NavItem
                                $active={currentPath === routes.BATTERY_BULK}
                                data-testid='battery-search-navigation-link'
                                to={routes.BATTERY_BULK}
                                $small
                                onClick={() => dispatch(toggleMobileNav(false))}
                            >
                                - BULK ACTIONS
                            </NavItem>
                        </AccessControl>
                        <AccessControl allow={permissions.battery_shipment_editor}>
                            <NavItem
                                $active={currentPath === routes.BATTERY_SHIPMENTS}
                                data-testid='battery-shipments-navigation-link'
                                to={routes.BATTERY_SHIPMENTS}
                                $small
                                onClick={() => dispatch(toggleMobileNav(false))}
                            >
                                - SHIPMENTS
                            </NavItem>
                        </AccessControl>
                    </>
                )}
                <ExpandableNavItem
                    to={routes.ZONES}
                    $expanded={isNavOpen}
                    $active={currentPath === routes.ZONES}
                    dataTestId='countries-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                    expandTestId='expanded-zones-navbar'
                    isExpanded={isZoneExpanded}
                    onExpandToggle={() => dispatch(expandZone(!isZoneExpanded))}
                    showExpandButton={isNavOpen}
                >
                    <NavIcon $expanded={isNavOpen} src={ZonesIcon} alt='' />
                    {isNavOpen && 'ZONES'}
                </ExpandableNavItem>
                {isZoneExpanded && isNavOpen && (
                    <>
                        <NavItem
                            $active={currentPath === routes.LEGAL}
                            data-testid='legal-entities-navigation-link'
                            to='/legal'
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - LEGAL ENTITIES
                        </NavItem>
                        <NavItem
                            $active={currentPath === routes.PRICING_PORTAL}
                            to={routes.PRICING_PORTAL}
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - PRICING
                        </NavItem>
                        <NavItem
                            $active={currentPath === routes.WORK_AREAS}
                            to={routes.WORK_AREAS}
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - WORK AREAS
                        </NavItem>
                        <NavItem
                            $active={currentPath === routes.STATION_CLEANUP_JOBS}
                            to={routes.STATION_CLEANUP_JOBS}
                            data-testid='station-cleanup-jobs-navigation-link'
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - PARKING CLEANUPS
                        </NavItem>
                        <NavItem
                            $active={currentPath === routes.PARKING_SNAPSHOT}
                            to={routes.PARKING_SNAPSHOT}
                            data-testid='parking-snapshot-navigation-link'
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - PARKING SNAPSHOT
                        </NavItem>
                    </>
                )}
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.CONFIGHUB}
                    to={routes.CONFIGHUB}
                    data-testid='confighub-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={TuneIcon} alt='' />
                    {isNavOpen && 'CONFIG HUB'}
                </NavItem>
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.CITYREGS}
                    to={routes.CITYREGS}
                    data-testid='cityregs-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={IconMarket} alt='' />
                    {isNavOpen && 'CITY REGS'}
                </NavItem>
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.PARTNER_PORTAL}
                    to={routes.PARTNER_PORTAL}
                    data-testid='partner-portal-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={PartnerIcon} alt='' />
                    {isNavOpen && 'PARTNERS'}
                </NavItem>
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.CAMPAIGNS}
                    to={routes.CAMPAIGNS}
                    data-testid='campaigns-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={CampaignsIcon} alt='' />
                    {isNavOpen && 'CAMPAIGNS'}
                </NavItem>
                <ExpandableNavItem
                    to={routes.REPAIR_MANAGEMENT}
                    $expanded={isNavOpen}
                    $active={currentPath === routes.REPAIR_MANAGEMENT}
                    dataTestId='rms-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                    expandTestId={isRepairsExpanded ? 'collapse-rms-navbar-btn' : 'expand-rms-navbar-btn'}
                    isExpanded={isRepairsExpanded}
                    onExpandToggle={() => dispatch(expandRepairs(!isRepairsExpanded))}
                    showExpandButton={isNavOpen}
                >
                    <NavIcon $expanded={isNavOpen} src={RepairIcon} alt='' />
                    {isNavOpen && 'REPAIRS'}
                </ExpandableNavItem>
                {isRepairsExpanded && isNavOpen && (
                    <>
                        <NavItem
                            $active={currentPath === routes.ADMINISTRATION}
                            data-testid='rms-administration-navigation-link'
                            to='/administration'
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - ADMINISTRATION
                        </NavItem>
                        <NavItem
                            $active={currentPath === routes.WAREHOUSES}
                            data-testid='warehouses-navigation-link'
                            to={routes.WAREHOUSES}
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - WAREHOUSES
                        </NavItem>
                        <NavItem
                            to='/maintenance-needs'
                            $active={currentPath === routes.MAINTENANCE_NEEDS}
                            data-testid='maintenance-needs-navigation-link'
                            $small
                            onClick={() => dispatch(toggleMobileNav(false))}
                        >
                            - MAINTENANCE NEEDS
                        </NavItem>
                    </>
                )}
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.PERMISSIONS}
                    to={routes.PERMISSIONS}
                    data-testid='permissions-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={PermissionsIcon} alt='' />
                    {isNavOpen && 'PERMISSIONS'}
                </NavItem>
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.ANALYTICS}
                    to={routes.ANALYTICS}
                    data-testid='analytics-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={AnalyticsIcon} alt='' />
                    {isNavOpen && 'ANALYTICS'}
                </NavItem>
                <NavItem
                    $expanded={isNavOpen}
                    $active={currentPath === routes.VOI_AGENT}
                    to={routes.VOI_AGENT}
                    data-testid='voi-agent-navigation-link'
                    onClick={() => dispatch(toggleMobileNav(false))}
                >
                    <NavIcon $expanded={isNavOpen} src={VoiAgentIcon} alt='' />
                    {isNavOpen && 'VOI AGENT'}
                </NavItem>
                <SupportSection expanded={isNavOpen} />
            </Content>
            {isNavOpen && <AnimatedScooter />}
        </NavigationWrapper>
    )
}

const NavigationWrapper = styled.div<{ $expanded: boolean; $isMobileNav?: boolean }>`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    position: relative;
    ${props =>
        props.$isMobileNav &&
        `position: fixed;
    top: 70px;
    left: 0;
    bottom: 0;
    z-index: ${zIndex.priority};`}
    width: ${props => (props.$isMobileNav ? '100vw' : props.$expanded ? '260px' : '70px')};
    transition: all 0.2s ease-in-out;
    background-color: ${colors.black};
    overflow: hidden auto;
`

const Content = styled.div`
    padding-top: 45px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`

export const NavItem = styled(Link, {
    shouldForwardProp: prop => !prop.startsWith('$'),
})<{
    $expanded?: boolean
    $active?: boolean
    $small?: boolean
    $hasExpandButton?: boolean
}>`
    display: flex;
    flex-direction: row;
    margin: 0 10px;
    padding: 10px;
    cursor: pointer;
    border-radius: 12px;
    align-items: center;
    opacity: ${props => (props.$active ? '1' : '0.5')};
    color: ${colors.white} !important;
    font-weight: 700;

    &:hover {
        opacity: 1;
    }
    ${fontSecondary}
    ${props =>
        props.$small &&
        `
        font-size: 12px;
        padding-left: 30px;
    `}

    background-color: ${props => (props.$active ? colors.grey50 : '')};
    transition: all 0.2s ease-in-out;
    flex: 1;
    justify-content: ${props => (props.$expanded || props.$small ? 'left' : 'center')};

    /* Add padding-right when expand button is present to make room for it */
    ${props => props.$hasExpandButton && 'padding-right: 50px;'}
`

const ExpandNavBtn = styled.div<{ $expanded: boolean }>`
    position: fixed;
    left: ${props => (props.$expanded ? '234px' : '42px')};
    transform: ${props => (props.$expanded ? '' : 'scaleX(-1)')};
    top: 80px;
    cursor: pointer;
    z-index: ${zIndex.priority};
    display: flex;
    transition: all 0.2s ease-in-out;
`

export const NavIcon = styled.img<{ $expanded: boolean }>`
    margin-right: ${props => (props.$expanded ? '20px' : '0')};
    width: 20px;
    height: 20px;
`

const CollapseImg = styled.img`
    width: 100%;
`
